import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue, set, update, get } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { formatDate } from '../../Utils/dateUtils';
import { FaArrowLeft, FaPrint, FaFileAlt, FaSearch, FaTimes, FaCheckCircle, FaClock, FaFileDownload } from 'react-icons/fa';
import GPEntryForm from './GPEntryForm';

const sanitizeKey = (key) => {
  return key.replace(/[.#$/[\]]/g, '_');
};

const GPRegisterView = () => {
  const { division } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const [company, setCompany] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [showGPForm, setShowGPForm] = useState(false);
  const [showFinalGPForm, setShowFinalGPForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [finalData, setFinalData] = useState({
    finalDate: new Date().toISOString().split('T')[0],
    finalVehicleNo: '',
    finalChallanNo: ''
  });
  const [address, setAddress] = useState('');
  const [editingJob, setEditingJob] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Fetch company and data
  useEffect(() => {
    const stateCompany = location.state?.company;
    const storedCompany = localStorage.getItem('selectedCompany') || localStorage.getItem('userCompany');
    const selected = stateCompany || storedCompany;

    if (selected) {
      // Must sanitize the same way GPEntryForm does so Firebase paths match
      const sanitizedCompany = sanitizeKey(selected.trim());
      setCompany(sanitizedCompany);
      localStorage.setItem('selectedCompany', sanitizedCompany);
    } else {
      setError('Company not found. Please login again.');
      navigate('/login');
    }
  }, [location, navigate]);

  // Fetch jobs and address
  useEffect(() => {
    if (!company || !division) return;

    const fetchJobs = async () => {
      setLoading(true);
      // Sanitize division to match GPEntryForm's write path
      const sanitizedDivision = sanitizeKey(division);
      const path = `companies/${company}/${sanitizedDivision}/gpRegister`;
      const registerRef = ref(realtimeDb, path);
      const addrRef = ref(realtimeDb, `companyDetails/${company}/address`);


      try {
        const addrSnap = await get(addrRef);
        setAddress(addrSnap.exists() ? addrSnap.val() : 'Address not available');

        const unsub = onValue(registerRef, (snapshot) => {
          const entries = snapshot.val() || {};
          const jobList = Object.entries(entries)
            .map(([jobNo, value]) => ({
              id: jobNo,
              jobNo: value.jobNo || 'N/A',
              kva: value.kva || 'N/A',
              make: value.make || 'N/A',
              makeSrNo: value.makeSrNo || 'N/A',
              type: value.type || 'N/A',
              oilCap: value.oilCap || '0.00',
              date: value.date || 'N/A',
              vehicleNo: value.vehicleNo || 'N/A',
              finalChallanNo: value.finalChallanNo || 'N/A',
              gpIssued: value.gpIssued || false,
              status: value.status || 'pending',
              gpCreatedAt: value.gpCreatedAt || '',
              completed: value.completed || false,
              finalDate: value.finalDate || 'N/A',
              finalVehicleNo: value.finalVehicleNo || 'N/A',
              srNo: value.srNo || 0,
              division: value.division || division,
              company: value.company || company
            }))
            .sort((a, b) => new Date(b.gpCreatedAt) - new Date(a.gpCreatedAt));

          setJobs(jobList);
          setLoading(false);
        }, (err) => {
          setError('Failed to fetch GP register: ' + err.message);
          setLoading(false);
        });

        return () => unsub();
      } catch (err) {
        setError('Failed to fetch address: ' + err.message);
        setLoading(false);
      }
    };

    fetchJobs();
  }, [company, division]);

  // Calculate filtered jobs
  const filteredJobs = jobs.filter(job => {
    const matchesSearch =
      job.jobNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.finalChallanNo && job.finalChallanNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      job.make.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filter === 'all' ||
      (filter === 'issued' && job.gpIssued) ||
      (filter === 'pending' && !job.gpIssued) ||
      (filter === 'completed' && job.completed);

    return matchesSearch && matchesStatus;
  });

  const handleJobSelection = (job) => {
    setSelectedJobs(prev => {
      if (prev.some(j => j.id === job.id)) {
        return prev.filter(j => j.id !== job.id);
      }
      return [...prev, job];
    });
  };

  const handleEditJob = (job) => {
    setEditingJob(job.id);
    setEditFormData({
      jobNo: job.jobNo,
      kva: job.kva,
      make: job.make,
      makeSrNo: job.makeSrNo,
      type: job.type,
      oilCap: job.oilCap,
      finalChallanNo: job.finalChallanNo,
      vehicleNo: job.vehicleNo,
      date: job.date
    });
  };

  const handleSaveEdit = async () => {
    try {
      const jobRef = ref(realtimeDb, `companies/${company}/${division}/gpRegister/${editingJob}`);
      await update(jobRef, editFormData);
      setEditingJob(null);
      setEditFormData({});
      setError(null);
    } catch (err) {
      setError('Failed to update job: ' + err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingJob(null);
    setEditFormData({});
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateFinalGP = () => {
    if (selectedJobs.length === 0) {
      setError('Please select at least one job');
      return;
    }
    setFinalData({
      finalDate: new Date().toISOString().split('T')[0],
      finalVehicleNo: selectedJobs[0]?.finalVehicleNo || '',
      finalChallanNo: selectedJobs[0]?.finalChallanNo || ''
    });
    setShowFinalGPForm(true);
  };

  const handleFinalGPSubmit = async (e) => {
    e.preventDefault();
    if (!finalData.finalDate || !finalData.finalVehicleNo) {
      setError('Please fill all fields');
      return;
    }

    try {
      const metaRef = ref(realtimeDb, `companies/${company}/metadata`);
      const metaSnap = await get(metaRef);
      let lastChallanNo = metaSnap.exists() ? metaSnap.val().lastChallanNo || 0 : 0;
      lastChallanNo += 1;
      const newChallan = `CHLN-${lastChallanNo}`;

      const batchUpdates = {};
      for (const job of selectedJobs) {
        const updateData = {
          ...job,
          finalDate: finalData.finalDate,
          finalVehicleNo: finalData.finalVehicleNo.toUpperCase(),
          finalChallanNo: newChallan,
          completed: true,
          status: 'completed'
        };

        batchUpdates[`companies/${company}/${division}/gpRegister/${job.id}`] = updateData;
        batchUpdates[`companies/${company}/${division}/gpEntryPass/${job.id}`] = updateData;
      }

      batchUpdates[`companies/${company}/metadata/lastChallanNo`] = lastChallanNo;

      await update(ref(realtimeDb), batchUpdates);

      setFinalData(prev => ({
        ...prev,
        finalChallanNo: newChallan
      }));

      setShowFinalGPForm(false);
      handlePrint();
    } catch (err) {
      setError('Failed to update final GP details: ' + err.message);
      console.error('Error in handleFinalGPSubmit:', err);
    }
  };

  const handlePrint = () => {
    const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Gate Pass - ${company}</title>
        <style>
          @page { 
            size: A4; 
            margin: 0;
          }
          body { 
            font-family: Arial; 
            margin: 0; 
            padding: 0;
            background-color: #fff;
          }
          .print-page { 
            width: 190mm;
            height: 277mm;
            padding: 15mm;
            margin: 10mm auto;
            box-sizing: border-box;
            border: 2px solid #000;
            position: relative;
          }
          @media print {
            body { 
              background-color: white;
              margin: 0;
            }
            .print-page {
              border: 2px solid #000 !important;
              box-shadow: none !important;
            }
          }
          .nav-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 10px 20px;
            background: #4F46E5;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            z-index: 1000;
          }
          @media print {
            .nav-button { display: none !important; }
          }
        </style>
      </head>
      <body>
        <button class="nav-button" onclick="window.close()">Back to Register</button>
        ${printRef.current.innerHTML}
      </body>
    </html>
  `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  const renderGatePass = (copyTitle) => (
    <div className="print-page" style={{
      width: '190mm',  // Reduced width to accommodate borders
      minHeight: '277mm', // Reduced height to accommodate borders
      padding: '15mm',
      margin: '10mm auto', // Center with margin
      boxSizing: 'border-box',
      pageBreakAfter: 'always',
      fontSize: '14pt',
      border: '2px solid #000', // Added page border
      boxShadow: '0 0 10px rgba(0,0,0,0.1)' // Optional subtle shadow
    }}>
      {/* Header Section */}
      <div className="header" style={{
        textAlign: 'center',
        marginBottom: '20px',
        borderBottom: '1px solid #000', // Header border
        paddingBottom: '10px'
      }}>
        <h1 style={{ fontSize: '28pt', fontWeight: 'bold', marginBottom: '10px' }}>{company}</h1>
        <div style={{ fontSize: '20pt', marginBottom: '10px' }}>Transformer Service</div>
        <div style={{ fontSize: '20pt', fontWeight: '600', marginBottom: '10px' }}>{division} Division Gate Pass</div>
        <div style={{ fontStyle: 'italic', fontSize: '16pt' }}>{address}</div>
      </div>

      {/* Title Section */}
      <div className="title" style={{
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '22pt',
        textDecoration: 'underline',
        marginBottom: '20px',
        padding: '5px',
        backgroundColor: '#f5f5f5' // Light background for title
      }}>
        GATE PASS - {copyTitle}
      </div>

      {/* Details Section */}
      <div className="details" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '15px',
        marginBottom: '25px',
        fontSize: '16pt',
        padding: '10px',
        border: '1px solid #ddd', // Light border around details
        borderRadius: '5px'
      }}>
        <div><strong>Final Date:</strong> {formatDate(finalData.finalDate) || 'N/A'}</div>
        <div><strong>Final Vehicle No:</strong> {finalData.finalVehicleNo || 'N/A'}</div>
        <div><strong>Challan No:</strong> {finalData.finalChallanNo || 'N/A'}</div>
        <div><strong>Total Oil:</strong> {selectedJobs.reduce((sum, job) => sum + (parseFloat(job.oilCap) || 0), 0).toFixed(2)} L</div>
      </div>

      {/* Table Section */}
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '30px',
        fontSize: '14pt',
        border: '1px solid #000'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5' }}>
            <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Sr. No.</th>
            <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Job No.</th>
            <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>KVA</th>
            <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Make</th>
            <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Make Sr. No.</th>
            <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Type</th>
            <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Oil Cap. (L)</th>
          </tr>
        </thead>
        <tbody>
          {selectedJobs.map((job, index) => (
            <tr key={index}>
              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{index + 1}</td>
              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{job.jobNo || 'N/A'}</td>
              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{job.kva || 'N/A'}</td>
              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{job.make || 'N/A'}</td>
              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{job.makeSrNo || 'N/A'}</td>
              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{job.type || 'N/A'}</td>
              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{job.oilCap || '0.00'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Signatures Section */}
      <div className="signatures" style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '60px',
        fontSize: '16pt',
        paddingTop: '20px',
        borderTop: '1px solid #000' // Border above signatures
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: '100px', margin: '10px auto' }}></div>
          <strong>Sign of Receiver</strong>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: '100px', margin: '10px auto' }}></div>
          <strong>Sign of {company}</strong>
        </div>
      </div>
    </div>
  );

  if (showGPForm && selectedJobs.length > 0) {
    return (
      <GPEntryForm
        initialJob={selectedJobs[0]}
        company={company}
        division={division}
        onBack={() => setShowGPForm(false)}
        onSubmitted={() => setShowGPForm(false)}
      />
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100">
            <FaArrowLeft className="text-gray-700 text-lg" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{division} Division</h1>
            <p className="text-lg font-medium text-gray-600">GP Register</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleGenerateFinalGP}
            disabled={selectedJobs.length === 0}
            className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg ${selectedJobs.length === 0 ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
              }`}
          >
            <FaFileDownload className="text-lg" />
            <span className="hidden sm:inline">Generate Final GP</span>
          </button>
          <button
            onClick={() => navigate(`/division/${encodeURIComponent(division)}/gp-history`)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
          >
            <FaFileAlt className="text-lg" />
            <span className="hidden sm:inline">View GP History</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 mb-6 text-red-700 bg-red-50 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Job No, Challan No or Make"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-600 font-medium">Filter:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Jobs</option>
              <option value="issued">GP Issued</option>
              <option value="pending">Pending GP</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-2">
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <FaCheckCircle className="text-green-500" />
              <span>{jobs.filter(j => j.gpIssued && !j.completed).length} Issued</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <FaClock className="text-yellow-500" />
              <span>{jobs.filter(j => !j.gpIssued).length} Pending</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <FaCheckCircle className="text-blue-500" />
              <span>{jobs.filter(j => j.completed).length} Completed</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-10">
            <div className="text-gray-500 mb-2">
              {searchTerm ? 'No matching jobs found' : 'No jobs found in GP Register'}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Action</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Job No</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">KVA</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Make</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Make Sr No</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Oil Cap</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Initial Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Initial Vehicle</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Final Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Final Vehicle</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Challan No</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Edit</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {job.gpIssued && !job.completed ? (
                        <button
                          onClick={() => handleJobSelection(job)}
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${selectedJobs.some(j => j.id === job.id)
                              ? 'bg-red-500 text-white'
                              : 'bg-green-500 text-white'
                            }`}
                        >
                          {selectedJobs.some(j => j.id === job.id) ? 'Deselect' : 'Select'}
                        </button>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.jobNo || ''}
                          onChange={(e) => handleEditInputChange('jobNo', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.jobNo
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.kva || ''}
                          onChange={(e) => handleEditInputChange('kva', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.kva
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.make || ''}
                          onChange={(e) => handleEditInputChange('make', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.make
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.makeSrNo || ''}
                          onChange={(e) => handleEditInputChange('makeSrNo', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.makeSrNo
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.type || ''}
                          onChange={(e) => handleEditInputChange('type', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.type
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.oilCap || ''}
                          onChange={(e) => handleEditInputChange('oilCap', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.oilCap
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="date"
                          value={editFormData.date || ''}
                          onChange={(e) => handleEditInputChange('date', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.date !== 'N/A' ? formatDate(job.date) : 'N/A'
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.vehicleNo || ''}
                          onChange={(e) => handleEditInputChange('vehicleNo', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.vehicleNo
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {job.finalDate !== 'N/A' ? formatDate(job.finalDate) : 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{job.finalVehicleNo}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <input
                          type="text"
                          value={editFormData.finalChallanNo || ''}
                          onChange={(e) => handleEditInputChange('finalChallanNo', e.target.value)}
                          className="w-full px-2 py-1 border rounded"
                        />
                      ) : (
                        job.finalChallanNo
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${job.completed ? 'bg-blue-100 text-blue-800' :
                          job.gpIssued ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {job.completed ? 'Completed' : job.gpIssued ? 'Issued' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingJob === job.id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveEdit}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditJob(job)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Hidden print content */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {selectedJobs.length > 0 && (
            <>
              {renderGatePass('Copy 1')}
              <div className="page-break"></div>
              {renderGatePass('Copy 2')}
            </>
          )}
        </div>
      </div>

      {showFinalGPForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Final Gate Pass Details</h3>
              <button onClick={() => setShowFinalGPForm(false)} className="text-gray-500 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleFinalGPSubmit}>
              <div className="grid grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Final Date*</label>
                  <input
                    type="date"
                    value={finalData.finalDate}
                    onChange={(e) => setFinalData({ ...finalData, finalDate: e.target.value })}
                    className="border p-2 rounded w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Final Vehicle No*</label>
                  <input
                    type="text"
                    value={finalData.finalVehicleNo}
                    onChange={(e) => setFinalData({ ...finalData, finalVehicleNo: e.target.value.toUpperCase() })}
                    className="border p-2 rounded w-full uppercase"
                    required
                  />
                </div>
              </div>

              {error && <div className="text-red-500 mb-4">{error}</div>}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowFinalGPForm(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                >
                  <FaPrint className="mr-2" />
                  Save & Print
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GPRegisterView;